import useAuthService from 'api/services/authService'
import useSearchAlertsService from 'api/services/searchAlertsService'
import { validateEmail } from 'api/utils/validateEmail'
import PropertyList from 'components/listings/Listings'
import { FormEvent, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Filters } from 'types/general'
import { ListedProperty } from 'types/property'

export type ListingsEmptySuggestion = {
  id: string
  title: string
  description: string
  ctaLabel: string
  onApply: () => void
}

type ListingsEmptyStateProps = {
  /** filtered = search returned 0; catalog = no listings published yet */
  mode?: 'filtered' | 'catalog'
  filterSummary: string[]
  criteria: Filters
  suggestions?: ListingsEmptySuggestion[]
  approximateProperties?: ListedProperty[]
  onEditFilters: () => void
  onClearFilters: () => void
  onFavorite?: (propertyId: string) => Promise<void>
  loadingFavoriteId?: string | null
}

const EmptyStateIllustration = () => (
  <div
    className="relative mx-auto mb-6 flex h-[88px] w-[88px] items-center justify-center rounded-2xl bg-[#E8EEF9]"
    aria-hidden
  >
    <span className="absolute right-2.5 top-2.5 h-2.5 w-2.5 rounded-full bg-[#7BC67E]" />
    <svg
      viewBox="0 0 48 48"
      className="h-11 w-11 text-imovPrimary"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="21" cy="21" r="9.5" stroke="currentColor" strokeWidth="2.5" />
      <path
        d="M28.5 28.5 36 36"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </svg>
    <span className="absolute bottom-3 left-3 flex h-5 w-5 items-center justify-center rounded-full bg-imovPrimary text-white shadow-sm">
      <svg viewBox="0 0 12 12" className="h-2.5 w-2.5" fill="none" aria-hidden>
        <path
          d="M3 3l6 6M9 3L3 9"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
        />
      </svg>
    </span>
  </div>
)

const BellIcon = () => (
  <svg viewBox="0 0 20 20" className="h-4 w-4 shrink-0" fill="currentColor" aria-hidden>
    <path d="M10 2a5 5 0 0 0-5 5v1.586l-.707.707A1 1 0 0 0 5 11h10a1 1 0 0 0 .707-1.707L15 8.586V7a5 5 0 0 0-5-5Zm0 14a2.5 2.5 0 0 0 2.45-2h-4.9A2.5 2.5 0 0 0 10 16Z" />
  </svg>
)

const ListingsEmptyState = ({
  filterSummary,
  criteria,
  suggestions = [],
  approximateProperties = [],
  onEditFilters,
  onFavorite,
  loadingFavoriteId,
}: ListingsEmptyStateProps) => {
  const { t } = useTranslation()
  const { isAuthenticated, user } = useAuthService()
  const { createAlert } = useSearchAlertsService()
  const userEmail =
    user && typeof user === 'object' && 'email' in user && typeof user.email === 'string'
      ? user.email
      : ''

  const [email, setEmail] = useState('')
  const [isSubmittingAlert, setIsSubmittingAlert] = useState(false)
  const [alertCreated, setAlertCreated] = useState(false)
  const [showAlertForm, setShowAlertForm] = useState(false)
  const emailInputRef = useRef<HTMLInputElement>(null)

  const summaryLabel = filterSummary.join(' · ')
  const hasApproximate = approximateProperties.length > 0
  const canCreateAlertDirectly = Boolean(isAuthenticated && userEmail)

  useEffect(() => {
    if (showAlertForm && !canCreateAlertDirectly) {
      emailInputRef.current?.focus()
    }
  }, [showAlertForm, canCreateAlertDirectly])

  const submitAlert = async () => {
    let submitEmail = userEmail.trim().toLowerCase()
    if (!isAuthenticated || !submitEmail) {
      submitEmail = email.trim().toLowerCase()
      if (validateEmail(submitEmail) !== true) {
        toast.error(String(t('listing.empty.alertInvalidEmail')))
        return
      }
    }

    setIsSubmittingAlert(true)
    try {
      const result = await createAlert({
        email: submitEmail,
        criteria,
        summaryLabel,
      })
      if (!result) {
        toast.error(String(t('listing.empty.alertFailed')))
        return
      }
      setAlertCreated(true)
      setShowAlertForm(false)
      setEmail('')
      toast.success(String(t('listing.empty.alertConfirmEmailSent')))
    } catch {
      toast.error(String(t('listing.empty.alertFailed')))
    } finally {
      setIsSubmittingAlert(false)
    }
  }

  const handleCreateAlertClick = () => {
    if (alertCreated) return
    if (canCreateAlertDirectly) {
      void submitAlert()
      return
    }
    setShowAlertForm(true)
  }

  const handleAlertSubmit = (e: FormEvent) => {
    e.preventDefault()
    void submitAlert()
  }

  return (
    <div className="space-y-8">
      <div className="rounded-2xl border border-gray-200 bg-white px-5 py-10 text-center sm:px-10 sm:py-12">
        <EmptyStateIllustration />

        <h2 className="text-xl font-bold tracking-tight text-imovSecondary sm:text-2xl">
          {t('listing.empty.catalogTitle')}
        </h2>
        <p className="mx-auto mt-3 max-w-lg text-sm leading-relaxed text-gray-500 sm:text-[15px]">
          {t('listing.empty.blankSubtitle')}
        </p>

        {alertCreated ? (
          <div className="mx-auto mt-8 max-w-md text-center">
            <p className="text-base font-semibold text-imovSecondary">
              {t('listing.empty.alertCreatedTitle')}
            </p>
            <p className="mt-2 text-sm leading-relaxed text-gray-600">
              {t('listing.empty.alertCreatedBody')}
            </p>
            <p className="mt-2 text-sm text-gray-500">{t('listing.empty.alertCheckInbox')}</p>
            <Link
              to={isAuthenticated ? '/account#my-space-alerts' : '/auth'}
              state={isAuthenticated ? undefined : { from: '/account#my-space-alerts' }}
              className="mt-5 inline-flex min-h-[44px] items-center justify-center rounded-full bg-imovPrimary px-5 py-2.5 text-sm font-semibold text-white hover:bg-imovPrimaryDark"
            >
              {t('listing.empty.alertSeeMyAlerts')}
            </Link>
          </div>
        ) : (
          <>
            <div className="mt-7 flex flex-col items-stretch justify-center gap-3 sm:flex-row sm:items-center">
              <button
                type="button"
                onClick={handleCreateAlertClick}
                disabled={isSubmittingAlert}
                className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-full bg-imovPrimary px-5 py-2.5 text-sm font-semibold text-white hover:bg-imovPrimaryDark disabled:opacity-50"
              >
                <BellIcon />
                {t('listing.empty.createAlertCta')}
              </button>
              <Link
                to="/create-ad"
                state={{ newListing: true }}
                className="inline-flex min-h-[44px] items-center justify-center rounded-full border border-gray-300 bg-white px-5 py-2.5 text-sm font-semibold text-imovSecondary hover:bg-gray-50"
              >
                {t('listing.empty.catalogCtaCreate')}
              </Link>
            </div>

            {showAlertForm && !canCreateAlertDirectly ? (
              <form
                onSubmit={handleAlertSubmit}
                className="mx-auto mt-6 flex max-w-sm flex-col gap-2 text-left"
              >
                <label className="sr-only" htmlFor="listing-alert-email">
                  {t('common.email')}
                </label>
                <input
                  ref={emailInputRef}
                  id="listing-alert-email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder={t('listing.empty.alertEmailPlaceholder')}
                  className="min-h-[44px] w-full rounded-xl border border-gray-300 px-3 py-2.5 text-sm text-imovSecondary placeholder:text-gray-400 focus:border-imovPrimary focus:outline-none focus:ring-2 focus:ring-imovPrimary/40"
                />
                <button
                  type="submit"
                  disabled={isSubmittingAlert}
                  className="min-h-[44px] rounded-full bg-imovSecondary px-4 py-2.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
                >
                  {t('listing.empty.alertSubmitCreate')}
                </button>
              </form>
            ) : null}
          </>
        )}

        {suggestions.length > 0 ? (
          <div className="mx-auto mt-8 max-w-md space-y-3 border-t border-gray-100 pt-6 text-left">
            {suggestions.map(suggestion => (
              <div
                key={suggestion.id}
                className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3"
              >
                <p className="text-sm font-semibold text-imovSecondary">{suggestion.title}</p>
                <p className="mt-1 text-sm text-gray-600">{suggestion.description}</p>
                <button
                  type="button"
                  onClick={suggestion.onApply}
                  className="mt-3 text-sm font-semibold text-imovPrimary hover:underline"
                >
                  {suggestion.ctaLabel}
                </button>
              </div>
            ))}
          </div>
        ) : null}
      </div>

      {hasApproximate ? (
        <div>
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h3 className="text-lg font-bold text-imovSecondary">
                {t('listing.empty.approximateTitle')}
              </h3>
              <p className="mt-1 text-sm text-gray-600">
                {t('listing.empty.approximateSubtitle')}
              </p>
            </div>
            <button
              type="button"
              onClick={onEditFilters}
              className="min-h-[44px] shrink-0 rounded-full border border-imovPrimary px-4 py-2 text-sm font-semibold text-imovPrimary hover:bg-imovPrimary/5"
            >
              {t('listing.empty.editCriteria')}
            </button>
          </div>
          <PropertyList
            properties={approximateProperties}
            handleFavorite={onFavorite}
            loadingFavoriteId={loadingFavoriteId}
            returnTo="/listings"
          />
        </div>
      ) : null}
    </div>
  )
}

export default ListingsEmptyState
